import { useState, useEffect } from 'react';
// Detect if running as PWA
function isPWA() {
    if (typeof window === 'undefined')
        return false;
    // Check for iOS standalone mode
    const isIOSPWA = window.navigator.standalone === true;
    // Check for Android/Chrome PWA
    const isAndroidPWA = window.matchMedia('(display-mode: standalone)').matches ||
        window.matchMedia('(display-mode: fullscreen)').matches ||
        window.matchMedia('(display-mode: minimal-ui)').matches;
    return isIOSPWA || isAndroidPWA;
}
// Get the actual available viewport height accounting for PWA mode
function getViewportHeight() {
    if (typeof window === 'undefined')
        return 800;
    // Try different measurements and pick the most appropriate one
    const measurements = {
        windowInnerHeight: window.innerHeight,
        documentClientHeight: document.documentElement.clientHeight,
        visualViewportHeight: window.visualViewport?.height,
    };
    // For PWA mode, prioritize visual viewport if available, otherwise window.innerHeight
    if (isPWA()) {
        return measurements.visualViewportHeight || measurements.windowInnerHeight;
    }
    // For browser mode, use the larger of documentElement or window measurements
    return Math.max(measurements.documentClientHeight || 0, measurements.windowInnerHeight || 0);
}
export function calculatePreviewHeight() {
    if (typeof window === 'undefined') {
        return '600px';
    }
    const viewportHeight = getViewportHeight();
    // Adjust calculation based on PWA vs browser mode
    if (isPWA()) {
        // In PWA mode, make minimal adjustments to preserve card size accuracy
        const scrollContainerHeight = viewportHeight - 64; // 809px (viewport - TopBar)
        const bottomBarHeight = 80; // BottomBar height  
        const padding = 4; // Very minimal padding
        const availableHeight = scrollContainerHeight - bottomBarHeight - padding;
        const minHeight = Math.max(availableHeight, 300);
        // Be less aggressive with small screen fallback
        if (minHeight > scrollContainerHeight * 0.85) {
            const smallScreenHeight = Math.max(scrollContainerHeight * 0.75, 250);
            return `${smallScreenHeight}px`;
        }
        return `${minHeight}px`;
    }
    else {
        // Browser mode - original calculation
        const topBarHeight = 64; // TopBar height
        const bottomBarHeight = 80; // BottomBar height  
        const padding = 32; // Top and bottom padding
        const availableHeight = viewportHeight - topBarHeight - bottomBarHeight - padding;
        const minHeight = Math.max(availableHeight, 300);
        if (minHeight > viewportHeight * 0.8) {
            const smallScreenHeight = Math.max(viewportHeight * 0.6, 250);
            return `${smallScreenHeight}px`;
        }
        return `${minHeight}px`;
    }
}
export function usePreviewHeight() {
    const [height, setHeight] = useState('600px');
    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const calculateHeight = () => {
            const newHeight = calculatePreviewHeight();
            setHeight(newHeight);
        };
        // Calculate initial height
        calculateHeight();
        // Listen for viewport changes (important for mobile)
        const handleResize = () => {
            // Use setTimeout to debounce rapid resize events
            setTimeout(calculateHeight, 100);
        };
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);
        // Also listen for visual viewport changes (iOS Safari)
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', handleResize);
        }
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleResize);
            }
        };
    }, []);
    return {
        height,
        heightClass: `h-[${height}]`
    };
}
// Calculate dimensions for scaled viewport
export function calculateScaledDimensions(scale, viewportWidth, viewportHeight) {
    return {
        width: viewportWidth * scale,
        height: viewportHeight * scale,
    };
}
